/**
 * El contrato del formulario: cómo se llaman los campos y qué puede devolver el
 * servidor cuando rechaza el envío.
 *
 * Vive aparte de `credenciales.server` a propósito, y no por gusto de ordenar:
 * el marco **prohíbe** que un módulo `.server` sea alcanzable desde el cliente,
 * y el componente necesita estos nombres para escribir el marcado. Meterlos en
 * el archivo de servidor arrastraba con ellos la validación y la futura llamada
 * al servicio de identidad hasta el paquete del navegador.
 *
 * La construcción falla si eso pasa, que es exactamente lo que tiene que hacer.
 */

/**
 * El usuario ES el documento de identidad. No hay variante de correo: en el
 * `DF-VIG-007` el identificador del trabajador y del personal del puesto médico
 * es el documento, y una pantalla que ofrece dos formas de identificarse le
 * pide a cada persona una decisión que no le toca tomar.
 */
export const CAMPO_DOCUMENTO = 'documento';
export const CAMPO_CLAVE = 'clave';
export const CAMPO_DESTINO = 'destino';

/**
 * LA FORMA DEL DOCUMENTO, Y VIVE AQUÍ PORQUE LA NECESITAN TRES.
 *
 * Ocho dígitos es el DNI peruano; el techo de doce deja sitio al carné de
 * extranjería sin inventar una regla por tipo de documento, que es una decisión
 * de negocio y no de esta pantalla.
 *
 * La validan **el servidor** —que es quien decide—, **el campo** —con su
 * `maxLength`, para que el navegador no deje escribir de más— y **el usuario
 * recordado** —que no guarda lo que no tiene esta forma—. Escrita tres veces
 * serían tres verdades que se separan el día que alguien cambie una.
 */
export const DOCUMENTO_MINIMO = 8;
export const DOCUMENTO_MAXIMO = 12;

/** Los dígitos, sin nada más: ni espacios, ni guiones, ni letras. */
const SOLO_DIGITOS = /^\d+$/u;

/**
 * Si un texto tiene la forma de un documento. **No dice que exista**: eso solo
 * lo sabe el servicio de identidad, y distinguirlo aquí sería decirle a
 * cualquiera qué documentos hay.
 */
export function esDocumento(valor: string): boolean {
  return (
    SOLO_DIGITOS.test(valor) && valor.length >= DOCUMENTO_MINIMO && valor.length <= DOCUMENTO_MAXIMO
  );
}

/** Claves de catálogo, no textos: el mensaje lo resuelve la interfaz. */
export type ClaveDeError =
  | 'acceso.error.faltaDocumento'
  | 'acceso.error.documentoInvalido'
  | 'acceso.error.faltaClave'
  | 'acceso.error.credenciales'
  | 'acceso.error.servicioNoDisponible'
  /** El cuerpo del envío no se pudo leer. No es culpa de quien escribe. */
  | 'acceso.error.envioIlegible'
  /**
   * 🆕 29/09/2026 · Las credenciales son buenas y su RBAC no le abre ninguna
   * aplicación. Decirlo no revela nada: quien lo lee acaba de demostrar quién es.
   */
  | 'acceso.error.sinAcceso';

export type CampoInvalido = typeof CAMPO_DOCUMENTO | typeof CAMPO_CLAVE;

export interface Rechazo {
  /** Mensaje del formulario entero. */
  readonly mensaje: ClaveDeError;
  /** Campos que quedan marcados como inválidos. */
  readonly campos: readonly CampoInvalido[];
  /** Se devuelve para repintar el formulario. La contraseña jamás. */
  readonly documento: string;
}
